import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Editor, toHTML, Toolbar } from 'ngx-editor';
import { BaseFormComponent } from '../../base/components/base-form.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MaskService } from '../services/mask.service';
import { cpfValidator } from '../services/cpf.validator';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Combo } from '../models/combo.model';
import { Ativo } from '../models/enumerations.model';
import { isEqual } from 'lodash';
import { EditFormTable } from '../models/edit-form.model';
import { idLocale } from 'ngx-bootstrap/chronos';

@Component({
  selector: 'app-edit-form',
  templateUrl: './edit-form.component.html',
  styleUrls: ['./edit-form.component.scss']
})
export class EditFormComponent<T> extends BaseFormComponent<T> implements OnInit, OnChanges {
  load:boolean = true;
  editor:Editor[] = [];
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify']
  ];

  dragItem: string = '';

  currentActive:Combo[] = Ativo;
  orderList:Combo[] = [{id:1,label:'1'}];
  previousValue:any;
  type:string = '';
  @Input() keyPermission:string = '';
  @Input() formData: EditFormTable[] = [];
  @Input() data:T = {} as T;
  @Input() validateForm: (data:T) => Record<string,any> = ()=> { return {} };
  @Input() canDelete: boolean = false;
  @Input() showActions: boolean = true;
  @Input() hasModal: boolean = false;
  @Input() openModal: (params: any) => void;
  @Output() formDataChange = new EventEmitter<FormGroup>();
  @Output() saveData = new EventEmitter<T>();
  @Output() deleteData = new EventEmitter<void>();
  previousOrderMap: { [key: number]: number } = {};

  constructor(
    public acRoute: ActivatedRoute,
    private router: Router,
    private maskService:MaskService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super(acRoute);
  }

  ngOnInit() {
    this.acRoute.paramMap.subscribe(params => {
      this.type = params?.get('type') ?? '';
    });
    this.initForm();
    this.form.valueChanges.subscribe(value => {
      const currentValue = this.form.getRawValue();
      currentValue['id'] = this.previousValue?.['id'];
      if (!isEqual(currentValue, this.previousValue)) {
        this.previousValue = this.form.value;
        this.formDataChange.emit(this.form);
      }
    });
    if (this.data) {
      this.form.patchValue(this.data);
      this.previousValue = this.form.value;
    }
    this.load = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.form) {
      this.data = changes['data'].currentValue;
      var dataList = this.data as { [key: string]: any };
      this.form.patchValue(dataList);
      this.formData.forEach(f => {
        if (f.type === 'listSlide') {
          const formArray = this.form.get(f.key) as FormArray;
          if (formArray && Array.isArray(dataList[f.key])) {
            formArray.controls.forEach((control) => {
              const formControl = control as FormControl;
              const isSelected = dataList[f.key].includes(formControl.value?.id);
              formControl.get('status')?.patchValue(isSelected);
            });
          }
        }
      });
      this.changeDetectorRef.detectChanges();
    }
    if (changes['formData'] && this.form) {
      this.formData = changes['formData'].currentValue;
      this.formData.reduce((a:{ [key: string]: FormControl | FormArray }, f) => {
        this.form.get(f.key)?.clearValidators();
        if (f.required) this.form.get(f.key)?.setValidators(Validators.required);
        if (f.maxLength) this.form.get(f.key)?.setValidators(Validators.maxLength(f.maxLength));
        if (f.minLength) this.form.get(f.key)?.setValidators(Validators.minLength(f.minLength));
        if (f.email) this.form.get(f.key)?.setValidators(Validators.email);
        if (f.min !== undefined && f.min !== null) this.form.get(f.key)?.setValidators(Validators.min(f.min));
        if (f.max !== undefined && f.max !== null) this.form.get(f.key)?.setValidators(Validators.max(f.max));
        if (f.type == 'cpf') this.form.get(f.key)?.setValidators(cpfValidator);
        this.form.get(f.key)?.updateValueAndValidity();
        return a;
      }, {} as { [key: string]: FormControl });
      this.changeDetectorRef.detectChanges();
    }
  }

  initForm(): void {
    const controls = this.formData.reduce((a:{ [key: string]: FormControl | FormArray }, f) => {
      const validators:any[] = [];
      if (f.required) validators.push(Validators.required);
      if (f.maxLength) validators.push(Validators.maxLength(f.maxLength));
      if (f.minLength) validators.push(Validators.minLength(f.minLength));
      if (f.email) validators.push(Validators.email);
      if (f.min !== undefined && f.min !== null) validators.push(Validators.min(f.min));
      if (f.max !== undefined && f.max !== null) validators.push(Validators.max(f.max));
      if (f.type == 'cpf') validators.push(cpfValidator);
      this.editor.push(new Editor());
      if(f.type == 'multSelect'|| f.type == 'multString' || f.type == 'dragList'){
        if (!f.value || !Array.isArray(f.value)) {
          a[f.key] = new FormArray([new FormControl(null, validators)]);
        }
        else{
          a[f.key] = new FormArray(f?.value?.map((item: { id: number }) => new FormControl(item.id, validators)));
        }
      }
      else if (f.type === 'multItems') {
        if (!f.value || !Array.isArray(f.value)) {
          a[f.key] = new FormArray([
            new FormGroup({
              id: new FormControl(null, validators),
              temp_id: new FormControl(0, validators),
              ordem: new FormControl(null, validators),
              item: new FormControl(null, validators),
              permiteBranco: new FormControl(null, validators)
            })
          ]);
        } else {
          a[f.key] = new FormArray(
            f.value.map((item: { id: number, ordem: number }) =>
              new FormGroup({
                id: new FormControl(item.id, validators),
                temp_id: new FormControl(item.id, validators),
                ordem: new FormControl(item.ordem, validators),
                item: new FormControl(item.ordem, validators),
                permiteBranco: new FormControl(item.ordem, validators),
              })
            )
          );
        }
      }
      else if(f.type === 'listSlide'){
        if (!f.options || !Array.isArray(f.options)) {
          a[f.key] = new FormArray([
            new FormGroup({})
          ]);
        } else {
          a[f.key] = new FormArray(
            (f.options as { id: number; status: boolean }[])?.map(
              (item: { id: number, status: boolean }) =>
                new FormGroup({
                  id: new FormControl(item.id, validators),
                  status: new FormControl(f.value?.includes(item.id) ?? false, validators),
                })
            )
          );
        }
      }
      else{
        var value =  f.value !== '' && f.value !== undefined ? f.value : null;
        a[f.key] = new FormControl(value, validators);
      }
      return a;
    }, {} as { [key: string]: FormControl });
    this.form = new FormGroup(controls);
  }

  onSaveData():void{
    this.changeDetectorRef.detectChanges();
    this.checkColors();
    var errors = this.validateForm(this.formatData());
    if (this.form.valid && Object.keys(errors).length == 0){
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.form.updateValueAndValidity();
        this.saveData.emit(this.formatData());
    } else {
      Object.keys(this.form.controls).forEach(field => {
        const control = this.form.get(field);
        if (control && control.invalid) {
          console.error(`Campo "${field}" está inválido.`);
          console.error(control.errors);
        }
      });
      Object.keys(errors).forEach(key => {
        const control = this.form.get(key);
        if (control) {
          const field = this.formData.find(f => f.key === key);
          if (field) {
            field.error = errors[key];
          }
          control.setErrors({ custom: errors[key] });
          control.markAsDirty();
          control.markAsTouched();
        }
      });
      this.markAsDirty();
    }
  }

  onDeleteData() {
    this.alertConfirmation().subscribe((confirmed) => {
      if (confirmed) {
        this.deleteData.emit();
      }
    });
  }


  checkColors():void{
    var data = this.form.value;
    this.formData = this.formData.map(item => {
      if(item.type === 'color'  && item.baseColor){
        const key = item.key as keyof T;
        const backgroundColor = data[key];
        if(backgroundColor){
          var isContrastOK  = this.checkContrast(backgroundColor, item.baseColor);
          if(isContrastOK === null){
            this.form.controls[item.key].setErrors({'hex': true});
          }
          else if(!isContrastOK){
            this.form.controls[item.key].setErrors({'contrast': isContrastOK});

          }
        }
      }
      return item
    })
  }

  compareFunction(o1: any, o2: any) {
    return o1 == o2;
  }

  private getLuminance(hex: string): number | null {
    const rgb = this.hexToRgb(hex);
    if(rgb === null){
      return null;
    }
    const lum = rgb.map(v =>{
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
      }
    );
    return 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
  } 

  hexToRgb(hex: string): number[] | null {
    const sanitizedHex = hex.replace("#", "");
  
    const hexRegex = /^(?:[0-9a-fA-F]{3}){1,2}$/;
    if (!hexRegex.test(sanitizedHex)) {
      return null;
    }
  
    const chunkLength = sanitizedHex.length === 3 ? 1 : 2;
    const rgb = [0, 1, 2].map(i => {
      const chunk = sanitizedHex.slice(i * chunkLength, (i + 1) * chunkLength);
      const value = chunkLength === 1 ? chunk + chunk : chunk;
      return parseInt(value, 16);
    });
  
    return rgb;
  }

  checkContrast(bgHex: string, textHex: string): boolean | null {
    const L1 = this.getLuminance(bgHex);
    const L2 = this.getLuminance(textHex);
    if(L1 === null || L2 === null){
      return null;
    }
    const contrastRatio = (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
    return contrastRatio >= 2; //recomendado é 4.5(texto pequeno) ou 3(texto grande) para garantir acessibilidade, mas podemos diminuir se pedirem 
  }
  
  onColorChange(key:string , event: Event): void {
    const input = event.target as HTMLInputElement;
    const hexValid = /^#([0-9A-Fa-f]{6})$/.test(input.value)
    if(hexValid){
      this.form.get(key)?.setValue(input.value);
    }
  }

  formatData():T{
    var data = this.form.value;
    const newData = this.formData.reduce((acc, item) => {
      const key = item.key as keyof T;
      if (item.type === 'select') {
        acc[key] = data[key]
        if (data[key]?.id)
          acc[key] = data[key].id
      }
      else if (item.type === 'datetime-local') {
        acc[key] = data[key]?.replace("T", " ");
      }
      else if (item.type === 'textEditor') {
        var value = '';
        try {
          if (data[key]?.content)
            value = toHTML(data[key]);
          else
            value = data[key]
        } catch (e) {
        }
        value = value?.replaceAll(`“`, `"`).replaceAll('’', "'");
        acc[key] = value as T[keyof T];
      } else {
        acc[key] = data[key];
      }
    
      return acc;
    }, {} as T);
    return newData;
  }

  back() {
    if(this.type == 'edit' || this.type == 'sub'){
      this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
    }
    else{
      this.router.navigate(['../'], { relativeTo: this.activatedRoute });
    }
  }

  
  
  maskPhone(key:string,event:Event):void{
    var value = (event.target as HTMLInputElement).value;
    var newValue = this.maskService.maskPhone(value);
    this.form.get(key)?.setValue(newValue ?? null);
  }
  
  maskCpf(key:string,event:Event):void{
    var value = (event.target as HTMLInputElement).value;
    var newValue = this.maskService.maskCpf(value);
    this.form.get(key)?.setValue(newValue ?? null);
  }
  maskMoney(key:string,event:Event){
    var value = (event.target as HTMLInputElement).value;
    var newValue = this.maskService.maskMoney(value);
    this.form.get(key)?.setValue(newValue ?? null);
  }

  maskInteger(key:string,event:Event){
    var value = (event.target as HTMLInputElement).value;
    var newValue = this.maskService.maskInteger(value);
    this.form.get(key)?.setValue(newValue ?? null);
  }

  getFormArray(field:string): FormArray {
    return this.form.get(field) as FormArray;
  }

  addItem(field:string){
    this.getFormArray(field).push(new FormControl(null));
  }

  addGroupItem(field:string){
    this.getFormArray(field).push(new FormGroup({
      id: new FormControl(null),
      temp_id: new FormControl(this.getFormArray(field)?.length ?? 0),
      ordem: new FormControl(null),
      item: new FormControl(null),
      permiteBranco: new FormControl(null)
    }));
    this.recalcOrder(field);
  }

  addDragItem(field:string){
    let data = this.form.value;

    if (this.dragItem != '' && !data[field].includes(this.dragItem)) {
      data[field].push(+this.dragItem);
      this.getFormArray(field).push(new FormControl(+this.dragItem));

      let objDados = this.formData.find(f => f.key == field)
      const maiorOrdem = Math.max(...objDados?.order.map(o => o.ordem));

      objDados?.order.push({id: +this.dragItem, ordem: maiorOrdem});

      this.dragItem = '';
    } else {
      this.msgError('Não é possível adicionar itens vazios ou repetidos');
    }
  }

  onSelectChange(selected: any) {
    if (selected) {
      this.dragItem = selected.id;
    } else {
      this.dragItem = '';
    }
  }
  
  recalcOrder(field:string){
    const qtd = this.getFormArray(field).length;
    const opcoes = Array.from({ length: qtd }, (_, i) => ({ id: i + 1, label: (i + 1).toString() }));
    this.orderList = opcoes;
  }

  valueOfObj(item: any, index: number) {
    var objId = item.value[index];
    return item?.options.find(o => o.id == objId)?.label;
  }

  orderOfObj(item:any, index:number){
		let data = this.form.value;
    item.value = data.objetivos_estrategicos;

    item?.order.map(o => {
      let idx = item.value.findIndex(v => v == o.id);
      if (o.ordem != idx + 1) {
        o.ordem = idx + 1;
        this.changeDetectorRef.detectChanges();
      }
    });

    let itemOrder = item?.order.find(o => o.id == data.objetivos_estrategicos[index]);
    
    return itemOrder?.ordem;
  }

  removeItem(field:string, index:number){
    this.getFormArray(field).removeAt(index);
  }

  removeDragItem(field: string, index: number) {
    let objDados = this.formData.find(f => f.key == field);
    objDados.order = objDados?.order.filter(o=> o.ordem !== (index+1));

    this.getFormArray(field).removeAt(index);

    objDados.order.map(o => {
      let idx = objDados.value.findIndex(v => v == o.id);
      if (o.ordem != idx + 1) {
        o.ordem = idx + 1;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  orderItem(event: CdkDragDrop<AbstractControl[]>, key: string) {
    const formArray = this.form.get(key) as FormArray;
    if (formArray && event.previousIndex !== event.currentIndex) {
      moveItemInArray(formArray.controls, event.previousIndex, event.currentIndex);
      formArray.controls.forEach((ctrl, index) => {
        var temp_id = ctrl.get('temp_id')?.value;
        this.previousOrderMap[temp_id] = index + 1;
        ctrl.get('ordem')?.setValue(index + 1);
      });
    }
  }
  
  setOrdemItem(event: any, itemData: any, key: string) {
    var index = itemData.id;
    if (event?.id) {
      var value = {data:itemData, index:index - 1,
        previousIndex: (this.previousOrderMap[itemData.temp_id] ?? 0) - 1,
        currentIndex: event.id - 1};
      this.orderItem(value as any, key); 
    }
  }

  blockTyping(event: KeyboardEvent): void {
    event.preventDefault();
  }
  openDatePicker(event: MouseEvent): void {
    return //destivado temporaramente pq afeta no firefox
    const input = event.target as HTMLInputElement;
    input.showPicker?.();
  }

	drop(event: CdkDragDrop<string>, field: string) {
		let data = this.form.value;
		var previousIndex = data[field].findIndex(d => d === event.item.data);
		moveItemInArray(data[field], previousIndex, event.currentIndex);
			
		const formArray = this.getFormArray(field)
		const movedControl = formArray.at(event.previousIndex);

		formArray.removeAt(event.previousIndex);
		formArray.insert(event.currentIndex, movedControl);
	}

	valueNotInOptions(item: any): boolean {
		const control = this.form.get(item.key);
		const value = control?.value;

		if (value === null || value === undefined) {
			return false;
		}

		return !item.options?.some(opt => opt.id === value);
	}
  
  //detecta o ctrl+s, evita que o navegador faça o evento padrão e chama a função de salvar
  @HostListener('document:keydown.control.s', ['$event'])
  onCtrlS(event: KeyboardEvent): void {
    event.preventDefault(); 
    this.onSaveData();
  }
  //detecta se o formulário está manipulado e se  pessoa está atualizando a página ou saindo dela.
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload($event: BeforeUnloadEvent): void {
    if (this.form.dirty) {
      $event.preventDefault();
      $event.returnValue = '';
    }
  }
}
